import { makeAutoObservable } from 'mobx';
import { IUICfgUICompositeProductInfoElementUI, IUICfgComponent } from '@canvas-logic/clp-api-types';
import { IComponentProductData, IComponentTextData } from '@canvas-logic/clp-domain';
import { ComponentElementState } from '@/elements/ComponentElement/ComponentElementState';

// ============================================================================
// Mock Composite Product Info State
// ============================================================================

export interface MockCompositeProductInfoConfig {
  viewElement: IUICfgUICompositeProductInfoElementUI;
  productData: {
    productKey: string;
    texts: {
      name: string;
      description: string;
      shortDescription?: string;
    };
  };
}

let compositeProductInfoId = 0;

/**
 * Mock state for CompositeProductInfoUIElement.
 * Provides the same interface as CompositeProductInfoState
 * but with hardcoded mock data instead of API calls.
 */
export class MockCompositeProductInfoState {
  public id = 'mock-composite-product-info-' + ++compositeProductInfoId;

  elementKind = 'ui-element';
  elementType = 'composite-product-info';

  constructor(private config: MockCompositeProductInfoConfig) {
    makeAutoObservable(this);
  }

  get viewElement(): IUICfgUICompositeProductInfoElementUI {
    return this.config.viewElement;
  }

  get title(): string {
    return this.config.productData.texts.name;
  }

  get description(): string {
    return this.config.productData.texts.description;
  }

  get shortDescription(): string | undefined {
    return this.config.productData.texts.shortDescription;
  }
}

// ============================================================================
// Mock Composite Product API (for ProductMergeContainer)
// ============================================================================

/**
 * Mock implementation of ICompositeProductApi for creating ComponentElementState
 * instances that can be used with ProductsContainerCfgUIElement.
 *
 * ProductsContainerCfgUIElement uses `instanceof ComponentElementState` check,
 * so we need real state instances with a mock API.
 */
export class MockCompositeProductApi {
  constructor(
    private componentKey: string,
    private products: IComponentProductData[],
    private textData: IComponentTextData = { name: 'Component', description: '' },
  ) {}

  isAvailable(_key: string) {
    return true;
  }
  getComponentAllProducts(_key: string) {
    return this.products;
  }
  getComponentTextData(_key: string) {
    return this.textData;
  }
  getComponentSchema(_key: string) {
    return { optional: false };
  }
  isEnable(_key: string) {
    return true;
  }
  loadAvailability(_key: string) {
    return Promise.resolve([true, false]);
  }
  setAvailability(_key: string, _value: boolean) {
    return Promise.resolve();
  }
  getComponentVariant(_key: string) {
    return null;
  }
  getAttributeValue(_componentKey: string, _attributeKey: string) {
    return undefined;
  }
  setAttributeValue(_componentKey: string, _attributeKey: string, _value: any) {
    return Promise.resolve();
  }
  loadCompatibleAttributeValues(_componentKey: string, _attributeKey: string) {
    return Promise.resolve([]);
  }
}

export interface MockComponentForMergeConfig {
  componentKey: string;
  viewElement: IUICfgComponent;
  products: IComponentProductData[];
  textData?: IComponentTextData;
}

/**
 * Creates a real ComponentElementState instance with a mock API.
 * Required for ProductsContainerCfgUIElement which uses instanceof check.
 */
export function createMockComponentStateForMerge(config: MockComponentForMergeConfig): ComponentElementState {
  const mockApi = new MockCompositeProductApi(config.componentKey, config.products, config.textData);
  return new ComponentElementState(mockApi as any, config.viewElement);
}

// ============================================================================
// Mock Component Lookup
// ============================================================================

export interface MockComponentLookupConfig {
  componentKey: string;
  isOptional: boolean;
  isEnabled: boolean;
  name: string;
  description?: string;
}

export class MockComponentLookup {
  texts: { name: string; description?: string };
  values: Array<{ id: string; value: boolean }>;
  current: { value: boolean } | null;
  componentSchema: { optional: boolean };
  productData: Array<{ productId: string }> = [];
  loading = false;

  constructor(config: MockComponentLookupConfig) {
    this.texts = { name: config.name, description: config.description };
    this.values = [
      { id: 'enabled', value: true },
      { id: 'disabled', value: false },
    ];
    this.current = { value: config.isEnabled };
    this.componentSchema = { optional: config.isOptional };
    makeAutoObservable(this);
  }

  select = (id: string) => {
    this.current = { value: id === 'enabled' };
  };
}
